import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Clock,
  Database,
  Users,
  BarChart3,
  CheckCircle,
  BookOpen,
  Download,
  Globe,
  Zap,
  FileText,
  Award,
  Shield,
  TrendingUp,
  Archive,
  Filter,
} from "lucide-react";
import { useEffect } from "react";

const AcademicResearchLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for Academic Researchers & Think Tanks - Longitudinal Public Opinion Data";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[105px] md:pb-24 overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-slate-700">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/nationcard-choose-informed.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 via-blue-700/85 to-slate-700/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <BookOpen size={16} className="mr-2" />
                    <span>Trusted by Leading Universities</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-cyan-300">
                        Academic Researchers
                      </span>{" "}
                      & <span className="text-blue-300">Think Tanks</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Access clean, longitudinal public-opinion data for robust,
                      publishable research.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-cyan-300">
                      Research-grade datasets with built-in ethics compliance
                      and validation
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-blue-900 h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href = "/solutions/academic-research")
                      }
                    >
                      Request Research Access
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Download Sample Dataset
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>100+ Nations</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Archive size={16} />
                      <span>5+ Years of Data</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                      <span>Research-Grade Quality</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                          <Database size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Data Archive
                          </div>
                          <div className="text-white/60 text-sm">
                            Research Interface
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Charts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Dataset Size
                        </div>
                        <div className="text-cyan-400 text-2xl font-bold">
                          2.4M
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="bg-cyan-400 h-2 rounded-full w-11/12"></div>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Countries
                        </div>
                        <div className="text-blue-400 text-2xl font-bold">
                          127
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          Active datasets
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Recent Exports
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Democracy index .csv</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                          <span>Climate sentiment .json</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span>Economic confidence .xlsx</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating verification indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Data Quality</div>
                  <div className="text-cyan-300 font-bold">99.7%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Research Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Research Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Traditional polling methods create barriers to rigorous academic
                research
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <BarChart3 className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    High Polling Costs
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Custom polling expenses consume research budgets and limit
                    study scope
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Database className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Longitudinal Data Gaps
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Difficulty obtaining reliable datasets spanning multiple
                    years and regions
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Clock className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Data Cleaning Overhead
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Time-consuming validation and cleaning delays research
                    progress
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Zap className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Integration Barriers
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Lack of seamless connection to SPSS, R, Python, and analysis
                    workflows
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Power Your Studies Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How We Power Your Studies
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform your research with enterprise-grade data
                infrastructure built for academia
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <Archive className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Timestamped Sentiment Archives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Download clean, date-stamped datasets spanning years and
                    regions with granular filtering and demographic breakdowns.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyan-200 transition-colors">
                    <Shield className="h-8 w-8 text-cyan-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Machine-Verified Data Quality
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Built-in ML filters and identity verification ensure
                    research-grade integrity with automated compliance
                    reporting.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
                    <Download className="h-8 w-8 text-slate-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Flexible API & Exports
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Connect directly to SPSS, R, Python, or your custom
                    workflows with RESTful APIs and multiple export formats.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Key Benefits & Capabilities
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Globe className="h-6 w-6 text-blue-600 flex-shrink-0" />
                <span className="font-medium">
                  Multi-country panel data from 100+ nations
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Filter className="h-6 w-6 text-cyan-600 flex-shrink-0" />
                <span className="font-medium">
                  Granular demographic and issue-specific filtering
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <FileText className="h-6 w-6 text-slate-600 flex-shrink-0" />
                <span className="font-medium">
                  Automated ethics and compliance reporting
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Award className="h-6 w-6 text-blue-600 flex-shrink-0" />
                <span className="font-medium">
                  Volume discounts and academic licensing
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Study Snapshot Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
              <CardHeader>
                <div className="text-center">
                  <Badge className="mb-4 bg-blue-100 text-blue-700">
                    Featured Study Snapshot
                  </Badge>
                  <CardTitle className="text-2xl md:text-3xl text-nation-blue">
                    Global Trust in Institutions: A 5-Year Analysis
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      500K+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Survey responses analyzed
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-600 mb-2">
                      20
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Countries compared cross-nationally
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-600 mb-2">
                      5 Years
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Longitudinal data coverage
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    Download White Paper
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Academic & Think-Tank Endorsements Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-nation-blue mb-8">
                Trusted by Leading Universities & Research Centers
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60 mb-12">
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Oxford University
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Brookings Institution
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Stanford Research
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Pew Research Center
                </div>
              </div>

              <Card className="max-w-2xl mx-auto hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8">
                  <blockquote className="text-lg italic text-muted-foreground mb-6">
                    "NationCard™ has revolutionized our comparative democracy
                    research. The longitudinal data quality and cross-national
                    coverage enabled us to publish groundbreaking findings on
                    institutional trust patterns across 40 countries over five
                    years."
                  </blockquote>
                  <div className="text-center">
                    <div className="font-semibold">Dr. Amanda Richardson</div>
                    <div className="text-sm text-muted-foreground">
                      Professor of Political Science
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Stanford University
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-slate-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Get Research Access Today
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to accelerate your research with clean, longitudinal public
              opinion data? Join leading academic institutions already using
              NationCard™.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-blue-600 hover:bg-white/90 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Request Research Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Schedule Data Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AcademicResearchLanding;
